
import { useState } from 'react';
import { readExcelFile } from '@/utils/excel/core/fileReader';
import { parseLocalShopPricesFromExcel } from '@/utils/excel/parsers/localShopParser';
import { CreateLocalShopPriceInput } from '@/types/database/local-shop-prices';
import { useBulkCreateLocalShopPrices } from '@/hooks/use-local-shop-prices';

export type UploadStep = 'select' | 'preview' | 'uploading' | 'complete';

export const useLocalShopExcelUpload = () => {
  const [step, setStep] = useState<UploadStep>('select');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<CreateLocalShopPriceInput[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  const bulkUpload = useBulkCreateLocalShopPrices();

  const processFile = async (file: File) => {
    console.log('Starting file processing for:', file.name);
    setSelectedFile(file);
    setIsProcessing(true);
    setParsedData([]);
    setParseErrors([]);

    try {
      const workbook = await readExcelFile(file);
      const { data, errors } = parseLocalShopPricesFromExcel(workbook);
      
      console.log('File processing completed');
      console.log('Parsed data count:', data.length);
      console.log('Errors count:', errors.length);
      
      setParsedData(data);
      setParseErrors(errors);
      
      if (data.length > 0 && errors.length === 0) {
        console.log('Moving to preview step');
        setStep('preview');
      } else if (errors.length > 0) {
        console.log('Staying in select step due to errors');
        setStep('select');
      }
      
    } catch (error) {
      console.error('Error processing file:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to process file';
      setParseErrors([errorMessage]);
      setParsedData([]);
      setStep('select');
    } finally {
      setIsProcessing(false);
    }
  };

  const uploadData = async () => {
    if (parsedData.length === 0) return;

    setStep('uploading');
    setUploadProgress({ current: 0, total: parsedData.length });
    
    try {
      await bulkUpload.mutateAsync(parsedData);
      setUploadProgress({ current: parsedData.length, total: parsedData.length });
      setStep('complete');
    } catch (error) {
      console.error('Upload failed:', error);
      setStep('preview');
    }
  };

  const reset = () => {
    setStep('select');
    setSelectedFile(null);
    setParsedData([]);
    setParseErrors([]);
    setUploadProgress({ current: 0, total: 0 });
  };

  return {
    step,
    selectedFile,
    parsedData,
    parseErrors,
    isProcessing,
    uploadProgress,
    processFile,
    uploadData,
    reset,
    hasData: parsedData.length > 0,
    hasErrors: parseErrors.length > 0,
  };
};
